import React, { useCallback, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { createLocale } from 'locales';

import Typography from 'components-new/typography/typography';
import Button from 'components-new/button/button';
import { showErrorToast, showSuccessToast } from 'components-new/toast/toast';

import { STATIC_FILES_URL } from 'constants/signup-flow-constants';

import type { Document } from 'state/__generated__/tadaahAPI';
import useGetFreelancerDiplomas from 'state/remote/remote-freelancer/use-get-freelancer-diplomas';

import FreelancerSignUpPageContainer from '../components/freelancer-sign-up-page-container';
import DiplomasInformationDialog from '../components/diplomas-information-dialog';

import freelancerRoutes from '../freelancer-routes';

import DiplomaList from './DiplomaList';

import DeleteDiplomaDialog from './DeleteDiplomaDialog';

const localize = createLocale('freelancerDiplomasInformationPageLocales');
const globlaLocalize = createLocale('global');

const DiplomasInformation: React.FC = () => {
  const videoUrl = `${STATIC_FILES_URL}videos/signupflow/h7.mp4`;
  const videoPreviewImageURL = `${window.STATIC_URL}assets/signupflow/h7.png`;

  const [currentDiploma, setCurrentDiploma] = useState({} as Document);

  const [searchParams, setSearchParams] = useSearchParams({});

  const { data } = useGetFreelancerDiplomas();

  const navigate = useNavigate();

  const closeDeleteDiplomaDialog = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const navigateToAddDiplomaPage = useCallback(() => {
    setSearchParams({});
    navigate(freelancerRoutes.diplomasYearSelection.path);
  }, [navigate, setSearchParams]);

  const goBackToFillInformation = useCallback(() => {
    setSearchParams({});
    navigate(freelancerRoutes.fillInformation.path);
  }, [navigate, setSearchParams]);

  const navigateToCertificatesinformationPage = useCallback(() => {
    navigate(
      `${freelancerRoutes.certificationInformation.path}?showVideoInstructions=true`,
    );
  }, [navigate]);

  const handleDeleteDiploma = useCallback(
    (item: Document) => {
      setCurrentDiploma(item);
      setSearchParams({ confirmDeleteDiploma: 'true' });
    },
    [setSearchParams, setCurrentDiploma],
  );

  const onDeleteError = useCallback(
    (error: Error) => {
      showErrorToast({ description: error.message, title: 'Error' });
      closeDeleteDiplomaDialog();
    },
    [closeDeleteDiplomaDialog],
  );

  const onDeleteSuccess = useCallback(() => {
    showSuccessToast({
      description: localize('diplomaDeleted'),
      title: globlaLocalize('notification'),
    });
    closeDeleteDiplomaDialog();
  }, [closeDeleteDiplomaDialog]);

  const shouldOpenDialog = !!(
    searchParams.get('confirmDeleteDiploma') &&
    searchParams.get('confirmDeleteDiploma') === 'true'
  );

  return (
    <FreelancerSignUpPageContainer
      showHeader
      headerTitle={localize('title')}
      showGoBackButton
      onGoBackClick={goBackToFillInformation}
      dialogInstructions={{
        component: <DiplomasInformationDialog />,
        shouldShowDialogInstructions: !data?.length,
      }}
      videoInstructions={{
        url: videoUrl,
        videoPreviewImageURL,
      }}
    >
      <>
        <Typography as="p" variant="body-medium" className="text-gray500 mt-6">
          {localize('helperMessage')}
        </Typography>

        <DiplomaList handleDeleteDiploma={handleDeleteDiploma} />
        <Button
          label={localize('addDiploma')}
          variant="secondary"
          className="w-full mt-6 justify-center"
          onClick={navigateToAddDiplomaPage}
        />
        <Button
          label={globlaLocalize('nextStep')}
          variant="primary"
          className="w-full mt-8 mb-10 justify-center"
          onClick={navigateToCertificatesinformationPage}
        />
      </>

      <DeleteDiplomaDialog
        onDeleteError={onDeleteError}
        onDeleteSuccess={onDeleteSuccess}
        diplomaName={currentDiploma.name || ''}
        isOpen={shouldOpenDialog}
        currentDiplomaId={currentDiploma?.id?.toString() || ''}
        onCloseClicked={closeDeleteDiplomaDialog}
      />
    </FreelancerSignUpPageContainer>
  );
};
export default DiplomasInformation;
